/**
 * Мелочи раздела «Проекты»: текст с разметкой ВК, лёгкий разбор описания (его часто вставляют из заявки
 * в markdown — заголовки «##», списки, **жирный») и меню действий строки «⋯».
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { tokenizeVkText } from "@impuls/shared";

/* ================= Текст с разметкой ВК ================= */

/** [club123|текст] — ссылкой, #хэштеги и обычные ссылки — как есть. */
export function ProjectsVkText({ text }: { text: string }) {
  const segs = useMemo(() => tokenizeVkText(text), [text]);
  return (
    <>
      {segs.map((s, i) =>
        s.type === "mention" || s.type === "url" ? (
          <a key={i} className="proj-ref" href={s.href} target="_blank" rel="noreferrer">
            {s.text}
          </a>
        ) : (
          <Fragment key={i}>{s.text}</Fragment>
        ),
      )}
    </>
  );
}

/** **жирный** внутри строки; остальное — через разметку ВК. */
function Inline({ text }: { text: string }) {
  const parts = text.replace(/`/g, "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") && p.length > 4 ? (
          <strong key={i}>
            <ProjectsVkText text={p.slice(2, -2)} />
          </strong>
        ) : (
          <ProjectsVkText key={i} text={p.replace(/\*\*/g, "")} />
        ),
      )}
    </>
  );
}

/* ================= Описание проекта ================= */

type Block = { kind: "h"; text: string } | { kind: "p"; lines: string[] } | { kind: "ul" | "ol"; items: string[] };

function parseBlocks(src: string): Block[] {
  const out: Block[] = [];
  let open = false;
  for (const raw of src.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      open = false;
      continue;
    }
    const h = /^#{1,6}\s+(.+)$/.exec(line);
    if (h) {
      out.push({ kind: "h", text: h[1] });
      open = false;
      continue;
    }
    const last = open ? out[out.length - 1] : undefined;
    const ul = /^[-*•]\s+(?:\[[ xX]\]\s+)?(.+)$/.exec(line);
    const ol = ul ? null : /^\d+[.)]\s+(.+)$/.exec(line);
    if (ul || ol) {
      const kind = ul ? "ul" : "ol";
      const item = (ul ?? ol)![1];
      if (last && last.kind === kind) last.items.push(item);
      else out.push({ kind, items: [item] });
      open = true;
      continue;
    }
    if (last?.kind === "p") last.lines.push(line);
    else if (last && (last.kind === "ul" || last.kind === "ol")) last.items[last.items.length - 1] += ` ${line}`;
    else out.push({ kind: "p", lines: [line] });
    open = true;
  }
  return out;
}

/** Есть ли в описании структура (заголовки, списки, абзацы), которую стоит показать в развёрнутом виде. */
export function projectsHasStructure(src: string): boolean {
  const blocks = parseBlocks(src);
  return blocks.length > 1 || blocks.some((b) => b.kind !== "p");
}

/** Сплошной текст для свёрнутой строки: без заголовков и маркеров списков. */
export function projectsExcerpt(src: string): string {
  const blocks = parseBlocks(src);
  const body = blocks.filter((b) => b.kind !== "h");
  return (body.length ? body : blocks)
    .map((b) => (b.kind === "h" ? b.text : b.kind === "p" ? b.lines.join(" ") : b.items.join(" ")))
    .join(" ")
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Полное описание с заголовками и списками. */
export function ProjectsRichText({ text }: { text: string }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return (
    <div className="proj-rich">
      {blocks.map((b, i) =>
        b.kind === "h" ? (
          <p key={i} className="proj-rich__h">
            <Inline text={b.text} />
          </p>
        ) : b.kind === "p" ? (
          <p key={i}>
            {b.lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                <Inline text={l} />
              </Fragment>
            ))}
          </p>
        ) : b.kind === "ul" ? (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j}>
                <Inline text={it} />
              </li>
            ))}
          </ul>
        ) : (
          <ol key={i}>
            {b.items.map((it, j) => (
              <li key={j}>
                <Inline text={it} />
              </li>
            ))}
          </ol>
        ),
      )}
    </div>
  );
}

/* ================= Меню действий строки ================= */

export interface ProjectsMenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export function ProjectsMenu({ label, items, note }: { label: string; items: ProjectsMenuItem[]; note?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    wrap.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btn.current) {
      // у нижнего края экрана (и над нижней панелью телефона) открываем вверх
      const r = btn.current.getBoundingClientRect();
      const tabbar = window.matchMedia("(max-width: 899px)").matches ? 64 : 0;
      setUp(r.bottom + 70 + items.length * 44 + (note ? 60 : 0) > window.innerHeight - tabbar);
    }
    setOpen((o) => !o);
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    let next = -1;
    if (e.key === "ArrowDown") next = (i + 1) % list.length;
    else if (e.key === "ArrowUp") next = (i - 1 + list.length) % list.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = list.length - 1;
    else if (e.key === "Tab") setOpen(false);
    if (next >= 0) {
      e.preventDefault();
      list[next]?.focus();
    }
  };

  return (
    <div className="proj-menu" ref={wrap}>
      <button
        ref={btn}
        type="button"
        className={`icon-btn proj-menu__btn ${open ? "is-open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={label}
        onClick={toggle}
      >
        <MoreHorizontal size={20} strokeWidth={1.9} />
      </button>
      {open && (
        <div id={menuId} role="menu" aria-label={label} className={`proj-menu__list ${up ? "is-up" : ""}`} onKeyDown={onMenuKey}>
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={`proj-menu__item ${it.danger ? "is-danger" : ""}`}
              aria-disabled={it.disabled || undefined}
              onClick={() => {
                if (it.disabled) return;
                setOpen(false);
                it.onSelect();
              }}
            >
              <it.icon size={17} strokeWidth={1.9} aria-hidden="true" />
              {it.label}
            </button>
          ))}
          {note && <p className="proj-menu__note">{note}</p>}
        </div>
      )}
    </div>
  );
}
